import { Tab, Tabs, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { BoxContainer } from "./styles";
import {
  appointmentColumns,
  callColumns,
  careColumns,
  dictationColumns,
  documentColumns,
  tabs,
} from "./helper";
import { useUserData } from "components/customHooks/userDataContext";
import dayjs from "dayjs";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { DataGridCustomPagination } from "components/helpers";
import { DocumentHeader } from "components/patients";
import {
  deleteDocument,
  getDocument,
  getPatientList,
  updateDocument,
} from "apiHandler/actions";
import { getMimeType } from "utils/helper";

const PatientDetails = () => {
  const { state } = useLocation();
  const [selectedTab, setSelectedTab] = useState(0);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [includeClaimState, setIncludeClaimState] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [patientDetails, setPatientDetails] = useState({});
  const userData = useUserData();
  const token = userData?.access_token;

  const handleIncludeClaimChange = (values, checked) => {
    setIncludeClaimState((prevState) => ({
      ...prevState,
      [values.id]: checked,
    }));
    onUpdate(values);
  };

  useEffect(() => {
    getPatients();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedTab]);

  useEffect(() => {
    onChangeTab();
  }, [selectedTab, downloading]);

  const getPatients = async () => {
    setLoading(true);
    try {
      const payload = {
        practice_id: `${userData?.selected_practice}`,
        patient_id: `${state.patient_id}`,
      };
      const response = await getPatientList(payload, token);
      if (response) {
        setPatientDetails(response[0]);
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const onChangeTab = () => {
    switch (selectedTab) {
      case 0:
        setColumns(appointmentColumns());
        break;
      case 1:
        setColumns(
          dictationColumns(
            currentPlaying,
            setCurrentPlaying,
            userData.selected_practice
          )
        );
        break;
      case 2:
        setColumns(
          documentColumns(
            onDelete,
            includeClaimState,
            handleIncludeClaimChange,
            getDocumentPdf,
            downloading
          )
        );
        break;
      case 3:
        setColumns(callColumns(currentPlaying, setCurrentPlaying));
        break;
      case 4:
        setColumns(careColumns());
        break;
      default:
        break;
    }
  };

  const fetchData = async () => {
    setData([]);
    const currentTab = tabs(userData.selected_practice, `${state.patient_id}`)[
      selectedTab
    ];
    if (currentTab?.fetchFunction) {
      setLoading(true);
      try {
        const response = await currentTab.fetchFunction(
          currentTab.payload,
          token
        );
        console.log("response>>: ", response);
        setData(response);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    } else {
      setData(null);
    }
  };

  const onDelete = async (values) => {
    try {
      const payload = {
        type: "Documents",
        practice_id: userData.selected_practice,
        patient_id: values.patient_id,
        id: values.id,
        s3_bucket: values.s3_bucket,
        s3_key: values.s3_key,
        user_name: userData.first_name + " " + userData.last_name,
      };
      const res = await deleteDocument(payload, token);
      console.log("res>>>: ", res);
      if (res && res.status === "Successful") {
        fetchData();
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const onUpdate = async (values) => {
    setLoading(true);
    try {
      const payload = {
        type: "Documents",
        practice_id: userData.selected_practice,
        charge_entry_id: values.charge_entry_id,
        document_id: values.id,
        document_type_id: values.document_type_id,
        filedescription: values.filedescription,
        document_scope: values.document_scope,
        patient_id: values.patient_id,
        encounter_id: values.encounter_id,
        payor_id: values.payor_id,
        include_claim: values.include_claim === "true" ? "false" : "true",
        user_name: userData.first_name + " " + userData.last_name,
      };

      const res = await updateDocument(payload, token);

      if (res && res.status === "Successful") {
        fetchData();
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentPdf = async (values) => {
    setDownloading(true);
    try {
      const payload = {
        type: "Documents",
        search_criteria: {
          document_id: values.id,
          practice_id: userData.selected_practice,
        },
        user_name: userData.first_name + " " + userData.last_name,
        practice_id: userData.selected_practice,
      };

      const response = await getDocument(payload, token);
      if (response) {
        const blob = new Blob([response], {
          type: getMimeType(values.fileextension),
        });
        const url = window.URL.createObjectURL(blob);

        window.open(url, "_blank");
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <BoxContainer>
      <Typography fontSize={16} fontWeight={500}>
        {state.first_name} {state.last_name} ({state.patient_id})
      </Typography>
      <Typography variant="body2">
        DOB: {dayjs(state.dob).format("MM/DD/YYYY")}
      </Typography>
      <Typography variant="body2">
        Number:
        <> Mobile: {patientDetails?.phone_no_mobile || "N/A"} </>
        <> | Home: {patientDetails?.phone_no_home || "N/A"} </>
        <> | Work: {patientDetails?.phone_no_work || "N/A"} </>
      </Typography>
      <Typography variant="body2">
        Email: {patientDetails?.email || "N/A"}
      </Typography>
      <Typography variant="body2">
        Address: {patientDetails?.address1 || "N/A"}{" "}
        {patientDetails?.address2 || ""}
      </Typography>

      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
      >
        {tabs().map((tab, index) => (
          <Tab key={index} label={tab.label} />
        ))}
      </Tabs>
      {selectedTab === 2 && (
        <DocumentHeader
          practiceId={userData.selected_practice}
          token={token}
          patientId={state.patient_id}
          userName={userData.first_name + " " + userData.last_name}
          fetchData={fetchData}
        />
      )}

      <DataGrid
        rows={data}
        columns={columns}
        autoHeight
        disableRowSelectionOnClick
        pagination
        pageSize={20}
        density="compact"
        sx={{ mt: 2 }}
        getRowId={(row) => row.id || row.patient_id}
        slots={{
          pagination: DataGridCustomPagination,
          toolbar: GridToolbar,
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "linear-progress",
          },
        }}
        loading={loading}
      />
    </BoxContainer>
  );
};

export default PatientDetails;
